import { useState, useEffect } from "react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import type { AssessmentFormData } from "./AssessmentForm"

export function useAssessment(onComplete?: () => void) {
  const [isLoading, setIsLoading] = useState(false)
  const [assessmentId, setAssessmentId] = useState<string | null>(null)
  const [defaultValues, setDefaultValues] = useState<AssessmentFormData | undefined>()
  const { toast } = useToast()

  useEffect(() => {
    const loadExistingAssessment = async () => {
      try {
        console.log("Starting to load existing assessment...")
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.user) {
          console.log("No authenticated session found during load")
          return
        }

        console.log("Authenticated user ID:", session.user.id)
        const { data: assessment, error } = await supabase
          .from('business_assessments')
          .select('*')
          .eq('user_id', session.user.id)
          .limit(1)
          .maybeSingle()

        if (error) {
          console.error("Database error when loading assessment:", error)
          throw error
        }

        if (assessment) {
          console.log("Found existing assessment:", assessment)
          setAssessmentId(assessment.id)
          // Type assertion with proper validation
          const formData = assessment.data as unknown as AssessmentFormData
          console.log("Setting form data:", formData)
          setDefaultValues(formData)
        } else {
          console.log("No existing assessment found for user")
        }
      } catch (error) {
        console.error("Error in loadExistingAssessment:", error)
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load your assessment. Please try again.",
        })
      }
    }

    console.log("Initializing assessment load...")
    loadExistingAssessment()
  }, [toast])

  const handleSubmit = async (values: AssessmentFormData) => {
    try {
      setIsLoading(true)
      console.log("Starting comprehensive assessment submission with values:", values)
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        console.log("No authenticated session found during submission")
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please sign in to save your assessment.",
        })
        return
      }

      console.log("Authenticated user ID for submission:", session.user.id)
      console.log("Current assessment ID:", assessmentId)

      // Convert AssessmentFormData to Json-compatible format with all fields
      const jsonData = {
        fullName: values.fullName,
        companyName: values.companyName,
        country: values.country,
        reason: values.reason,
        industry: values.industry,
        businessStage: values.businessStage,
        employeeCount: values.employeeCount,
        annualRevenue: values.annualRevenue,
        businessDescription: values.businessDescription,
        targetMarket: values.targetMarket,
        competitiveAdvantage: values.competitiveAdvantage,
        challenges: values.challenges,
        goals: values.goals,
        revenueModel: values.revenueModel,
        marketingChannels: values.marketingChannels,
        keyMetrics: values.keyMetrics
      }

      if (assessmentId) {
        console.log("Updating existing comprehensive assessment...")
        const { data, error } = await supabase
          .from("business_assessments")
          .update({
            data: jsonData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', assessmentId)
          .select()

        if (error) {
          console.error("Error updating assessment:", error)
          throw error
        }
        console.log("Comprehensive assessment updated successfully:", data)
      } else {
        console.log("Creating new comprehensive assessment...")
        const { data, error } = await supabase
          .from("business_assessments")
          .insert({
            user_id: session.user.id,
            data: jsonData,
          })
          .select()

        if (error) {
          console.error("Error creating assessment:", error)
          throw error
        }
        
        if (data && data[0]) {
          setAssessmentId(data[0].id)
        }
        console.log("Comprehensive assessment created successfully:", data)
      }

      // Trigger persona context refresh to share comprehensive assessment data
      window.dispatchEvent(new CustomEvent('assessmentUpdated', { detail: values }));

      toast({
        title: "Comprehensive Assessment Saved",
        description: "Your detailed business assessment has been saved! CSO Agent can now provide highly personalized, strategic recommendations based on your business context.",
      })

      if (onComplete) {
        console.log("Completing comprehensive assessment view...")
        onComplete()
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem saving your comprehensive assessment. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!assessmentId) {
      console.log("No assessment ID found for deletion")
      return
    }

    try {
      setIsLoading(true)
      console.log("Attempting to delete assessment:", assessmentId)
      
      const { error } = await supabase
        .from("business_assessments")
        .delete()
        .eq('id', assessmentId)

      if (error) {
        console.error("Error deleting assessment:", error)
        throw error
      }

      console.log("Assessment deleted successfully")
      toast({
        title: "Assessment deleted",
        description: "Your business assessment has been deleted successfully.",
      })

      setAssessmentId(null)
      setDefaultValues(undefined)
      
      // Trigger persona context refresh
      window.dispatchEvent(new CustomEvent('assessmentDeleted'));
      
      if (onComplete) {
        console.log("Completing assessment view after deletion...")
        onComplete()
      }
    } catch (error) {
      console.error("Error in handleDelete:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem deleting your assessment. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    assessmentId,
    defaultValues,
    handleSubmit,
    handleDelete,
  }
}
